// Özet karşılaştırma tablosu (M16 T14, mockup 01; kural 13 Formül kolonu = aktif varyant etiketi).
// Kolonlar: KPI | Formül | cari yıl | önceki yıl | Değişim | Veri durumu | Kontrol (✓ toggle).
// Satıra tıkla → Veri Girişi'nde o göstergeye git (?highlight=<code>, kural 12).

import {
  changePercent,
  type KpiResolvedCell,
  type KpiResolvedIndicator,
  type KpiTargetWithProgress,
} from "@mahir/shared";
import { useMemo } from "react";
import { useMediaQuery } from "../../hooks/use-media-query";
import { useTreeCollapsed } from "../../hooks/use-tree-collapsed";
import { buildTreeList, treeIndent, type TreeListRow } from "../../lib/kpi-tree-list";
import { KpiTreeBandRow, KpiTreeToolbar } from "./KpiTreeBandRow";
import {
  formatDistance,
  formatTargetCell,
  targetTone,
  targetToneColor,
} from "../../lib/kpi-targets-ui";
import { formatKpiCell, KPI_COPY } from "../../lib/kpi-ui";

/** `calcError` — DR5 "Hesaplanamayan toplam" sayacının süzgeci (sekme düğmesi yok, şeritten açılır) */
export type OzetTab = "all" | "missing" | "check" | "approval" | "calcError";

export interface KpiComparisonRow {
  indicator: KpiResolvedIndicator;
  cur: KpiResolvedCell | undefined;
  prev: KpiResolvedCell | undefined;
  /** kırılımdan türetilmiş toplam — kullanıcı doldurmaz: "eksik"e girmez (spec §6.6) */
  derivedTotal?: boolean;
}

export function rowMatchesTab(row: KpiComparisonRow, tab: OzetTab): boolean {
  const c = row.cur;
  switch (tab) {
    case "all":
      return true;
    case "missing":
      return (
        !row.derivedTotal &&
        (!c ||
          c.status === "empty" ||
          c.status === "pending_xxx" ||
          (c.status === "calc_error" && !!c.hasPending))
      );
    case "calcError":
      return c?.status === "calc_error" && !c.hasPending;
    case "check":
      return !row.indicator.checkedAt || c?.status === "calc_error";
    case "approval":
      return (
        c?.workflowState === "submitted" ||
        c?.workflowState === "clarification" ||
        c?.workflowState === "client_approved" // TODO-KPI-10: hâlâ danışman onayında
      );
  }
}

const th: React.CSSProperties = {
  padding: "8px 10px",
  fontSize: "0.72rem",
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  color: "var(--color-text-3)",
  borderBottom: "1px solid var(--color-border)",
  textAlign: "right",
  whiteSpace: "nowrap",
};
const td: React.CSSProperties = {
  padding: "8px 10px",
  borderBottom: "1px solid var(--color-border)",
  fontSize: "0.85rem",
  color: "var(--color-text)",
  textAlign: "right",
  fontVariantNumeric: "tabular-nums",
  whiteSpace: "nowrap",
};

function Pill({
  text,
  tone,
}: {
  text: string;
  tone: "ok" | "wait" | "xxx" | "muted";
}): JSX.Element {
  const map = {
    ok: {
      bg: "color-mix(in srgb, var(--color-primary) 10%, white)",
      fg: "var(--color-primary)",
      border: "transparent",
    },
    wait: {
      bg: "var(--color-warning-bg)",
      fg: "var(--color-warning-text)",
      border: "var(--color-warning-border)",
    },
    xxx: { bg: "var(--color-bg)", fg: "var(--color-text-2)", border: "var(--color-border)" },
    muted: { bg: "transparent", fg: "var(--color-text-3)", border: "transparent" },
  }[tone];
  return (
    <span
      style={{
        display: "inline-block",
        padding: "2px 9px",
        borderRadius: 999,
        fontSize: "0.66rem",
        fontWeight: 700,
        background: map.bg,
        color: map.fg,
        border: `1px solid ${map.border}`,
      }}
    >
      {text}
    </span>
  );
}

export function dataPill(cell: KpiResolvedCell | undefined): JSX.Element {
  if (!cell || cell.status === "empty") return <Pill text="BOŞ" tone="xxx" />;
  // Finar 17: durum hapı "DEĞER BEKLENİYOR" (hücre değeri yine XXX — K11)
  if (cell.status === "pending_xxx") return <Pill text={KPI_COPY.cell.pendingPill} tone="xxx" />;
  if (cell.status === "calc_error")
    return (
      <Pill text={cell.hasPending ? KPI_COPY.cell.pendingPill : "HESAPLANAMADI"} tone="wait" />
    );
  if (cell.workflowState === "submitted")
    return <Pill text={KPI_COPY.workflow.submitted} tone="wait" />;
  if (cell.workflowState === "clarification") return <Pill text="AÇIKLAMA İSTENDİ" tone="wait" />;
  if (cell.status === "not_measured") return <Pill text="ÖLÇÜLMÜYOR" tone="muted" />;
  return <Pill text="DOLU" tone="ok" />;
}

export function KpiComparisonTable(props: {
  rows: KpiComparisonRow[];
  year: number;
  prevYear: number | null;
  onOpen: (code: string) => void;
  onToggleChecked: (indicatorId: string, checked: boolean) => void;
  canCheck: boolean;
  /** T19/DR4 — portalda kontrol boyutu HİÇ görünmez (kolon render edilmez) */
  hideCheck?: boolean;
  /** TODO-KPI-01 — metricId → birincil hedef; verilirse ve boş değilse "Hedef" + "Uzaklık" kolonları eklenir. */
  targets?: Map<string, KpiTargetWithProgress>;
  /**
   * T7 — kategori ağacı: `rows` süzgeçten geçmiş satırlar, `allRows` tümü (bant sayaçları için),
   * `indicators` bant/derinlik kaynağı. Verilmezse (kategorisiz kullanım) düz liste.
   */
  tree?: {
    indicators: KpiResolvedIndicator[];
    allRows: KpiComparisonRow[];
    filtered: boolean;
    screen: string;
    /** test: ilk kapalı bantlar (depolama boşken) */
    initialCollapsed?: string[];
  };
}): JSX.Element {
  const { rows, year, prevYear, onOpen, onToggleChecked, canCheck } = props;
  const treeIndicators = props.tree?.indicators ?? [];
  const tc = useTreeCollapsed(
    props.tree?.screen ?? "ozet",
    treeIndicators,
    props.tree?.initialCollapsed,
  );
  const narrow = useMediaQuery("(max-width: 1024px)");
  const filtered = props.tree?.filtered ?? false;
  const list = useMemo<TreeListRow<KpiComparisonRow>[]>(() => {
    if (!props.tree)
      return rows.map((r) => ({ kind: "item", data: r, indicatorId: r.indicator.id, depth: 0 }));
    const visibleIds = new Set(rows.map((r) => r.indicator.id));
    return buildTreeList(
      props.tree.indicators,
      props.tree.allRows.map((r) => ({
        indicatorId: r.indicator.id,
        matches: visibleIds.has(r.indicator.id),
        data: r,
      })),
      { collapsed: tc.collapsed, filtered: props.tree.filtered },
    );
  }, [props.tree, rows, tc.collapsed]);
  const hasBands = list.some((r) => r.kind === "band");
  const hideCheck = props.hideCheck ?? false;
  const targets = props.targets && props.targets.size > 0 ? props.targets : null;
  const colCount = (targets ? 9 : 7) - (hideCheck ? 1 : 0) - (prevYear ? 0 : 1);
  return (
    // P2-3: görünür yatay kaydırma + sağa yapışık KONTROL kolonu (1440'ta ekran dışında kalıyordu)
    <div className="kpi-scroll-x">
      {hasBands ? (
        <KpiTreeToolbar onCollapseAll={tc.collapseAll} onExpandAll={tc.expandAll} />
      ) : null}
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr>
            <th style={{ ...th, textAlign: "left" }}>KPI</th>
            <th style={{ ...th, textAlign: "left" }}>Formül</th>
            <th style={{ ...th, color: "var(--color-primary)" }}>{year}</th>
            {prevYear ? <th style={th}>{prevYear}</th> : null}
            <th style={th}>Değişim</th>
            {targets ? <th style={th}>Hedef</th> : null}
            {targets ? <th style={th}>Uzaklık</th> : null}
            <th style={{ ...th, textAlign: "left" }}>Veri</th>
            {!hideCheck ? (
              <th className="kpi-sticky-right" style={{ ...th, textAlign: "left" }}>
                Kontrol
              </th>
            ) : null}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td
                colSpan={colCount}
                style={{ ...td, textAlign: "center", color: "var(--color-text-3)", padding: 28 }}
              >
                Bu sekmede gösterge yok.
              </td>
            </tr>
          ) : null}
          {list.map((entry) => {
            if (entry.kind === "band") {
              return (
                <KpiTreeBandRow
                  key={`band-${entry.indicator.id}`}
                  row={entry}
                  colSpan={colCount}
                  narrow={narrow}
                  filtered={filtered}
                  onToggle={tc.setBand}
                />
              );
            }
            const { indicator: ind, cur, prev } = entry.data;
            const isPct = ind.valueType === "percent";
            const active =
              ind.formulas.find((f) => f.id === ind.activeFormulaId) ??
              ind.formulas.find((f) => f.isActive);
            const pct =
              cur?.status === "filled" &&
              prev?.status === "filled" &&
              typeof cur.value === "number" &&
              typeof prev.value === "number"
                ? isPct
                  ? cur.value - prev.value
                  : changePercent(cur.value, prev.value)
                : null;
            const tone =
              pct === null || pct === 0 || ind.increaseIsGood === null
                ? "neutral"
                : pct > 0 === ind.increaseIsGood
                  ? "good"
                  : "bad";
            const checked = !!ind.checkedAt;
            return (
              <tr key={ind.id}>
                <td
                  style={{
                    ...td,
                    textAlign: "left",
                    maxWidth: 320,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  <button
                    type="button"
                    onClick={() => onOpen(ind.code)}
                    style={{
                      color: "var(--color-primary)",
                      textAlign: "left",
                      minHeight: 28,
                      marginLeft: treeIndent(entry.depth, narrow),
                    }}
                    title="Veri Girişi'nde aç"
                  >
                    {ind.label}
                  </button>
                </td>
                <td
                  style={{
                    ...td,
                    textAlign: "left",
                    color: "var(--color-text-2)",
                    fontSize: "0.75rem",
                    maxWidth: 200,
                    whiteSpace: "normal",
                  }}
                >
                  {ind.isDerived ? (active?.label ?? "—") : "Ham veri"}
                </td>
                <td style={td}>{formatKpiCell(cur, { percent: isPct }).text}</td>
                {prevYear ? (
                  <td style={{ ...td, color: "var(--color-text-2)" }}>
                    {formatKpiCell(prev, { percent: isPct }).text}
                  </td>
                ) : null}
                <td
                  style={{
                    ...td,
                    color:
                      tone === "good"
                        ? "var(--color-success)"
                        : tone === "bad"
                          ? "var(--color-error)"
                          : "var(--color-text-3)",
                    fontSize: "0.8rem",
                  }}
                >
                  {pct === null
                    ? "—"
                    : isPct
                      ? `${pct >= 0 ? "▲" : "▼"} ${Math.abs(pct).toLocaleString("tr-TR", { maximumFractionDigits: 1 })} puan`
                      : `${pct > 0 ? "↑" : pct < 0 ? "↓" : "→"} %${Math.abs(pct).toLocaleString("tr-TR", { maximumFractionDigits: 1 })}`}
                </td>
                {targets
                  ? (() => {
                      const t = targets.get(ind.metricId);
                      const tone = t ? targetTone(t.progress) : "unknown";
                      return (
                        <>
                          <td
                            style={{
                              ...td,
                              fontSize: "0.8rem",
                              color: t ? "var(--color-text)" : "var(--color-text-3)",
                            }}
                          >
                            {t ? formatTargetCell(t) : "—"}
                          </td>
                          <td
                            style={{
                              ...td,
                              fontSize: "0.8rem",
                              fontWeight: 600,
                              color: targetToneColor(tone),
                            }}
                          >
                            {t ? formatDistance(t) : "—"}
                          </td>
                        </>
                      );
                    })()
                  : null}
                <td style={{ ...td, textAlign: "left" }}>{dataPill(cur)}</td>
                {hideCheck ? null : (
                  <td
                    className="kpi-sticky-right"
                    style={{ ...td, textAlign: "left", fontSize: "0.78rem" }}
                  >
                    <button
                      type="button"
                      disabled={!canCheck}
                      aria-pressed={checked}
                      onClick={() => onToggleChecked(ind.id, !checked)}
                      style={{
                        color: checked ? "var(--color-success)" : "var(--color-warning-text)",
                        minHeight: 28,
                      }}
                      title={
                        checked
                          ? "Kontrol edildi — kaldırmak için tıkla"
                          : "Kontrol edildi olarak işaretle"
                      }
                    >
                      {checked
                        ? `✓ ${new Date(ind.checkedAt!).toLocaleDateString("tr-TR", { day: "2-digit", month: "2-digit" })}`
                        : "bekliyor"}
                    </button>
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
